import {
	Checkbox,
	Col,
	Collapse,
	DatePicker,
	Divider,
	Form,
	FormInstance,
	Input,
	InputNumber,
	Radio,
	Row,
	Select,
	Modal,
	Table,
	Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, FileSearchOutlined } from '@ant-design/icons';
import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import QCSahamModal, { SahamFormValues } from './QCSahamModal';
import QCPemilikManfaatModal, { PemilikManfaatFormValues } from './QCPemilikManfaatModal';
import { rules } from '@/libs/antd/rules';
import { inputCurrency, inputRtRw } from '@/libs/antd/input-number';
import { useSelectAddress, useSelectByEndpoint } from '@/libs/fleet-api/hooks/master-data';
import { useQCPraPencairanData } from '@/pages/robotic/qc-pra-pencairan/QCPraPencairanContext';
import { FIELD_API_PATHS } from '@/pages/robotic/qc-pra-pencairan/qc-pd-mapping';

type AddressMasterData = {
	postalId: number;
	postalCode: string;
	locationName: string;
	districtName: string;
	cityName: string;
};

export type FieldConfig = {
	name: string;
	label: string;
	type: 'text' | 'number' | 'currency' | 'select' | 'date' | 'textarea' | 'divider' | 'radio' | 'spacer' | 'rt-rw';
	required?: boolean;
	disabled?: boolean;
	options?: { label: string; value: string }[];
	span?: number;
	sourceEndpoint?: string;
};

/**
 * Sibling field map for postal lookup. When a user picks a postal code via the master-address
 * endpoint, we also push the matching kelurahan / kecamatan / kota strings into the sibling
 * form fields. Convention: a `kodePos<Suffix>` field has sibling `kelurahan<Suffix>`, etc.
 */
const POSTAL_ENDPOINT_PATH = '/master-address/get-list';
const isPostalEndpoint = (endpoint: string): boolean =>
	endpoint.replace(/^\/api\//, '/') === POSTAL_ENDPOINT_PATH;

const deriveAddressSiblings = (
	fieldName: string,
): { kelurahan: string; kecamatan: string; kota: string } | null => {
	if (!fieldName.startsWith('kodePos')) return null;
	const suffix = fieldName.slice('kodePos'.length);
	return {
		kelurahan: `kelurahan${suffix}`,
		kecamatan: `kecamatan${suffix}`,
		kota: `kota${suffix}`,
	};
};

const LookupSelect: FC<{
	endpoint: string;
	disabled?: boolean;
	value?: string;
	onChange?: (value: string | undefined) => void;
	id?: string;
}> = ({ endpoint, disabled, value, onChange, id }) => {
	const selectProps = useSelectByEndpoint(endpoint);
	const form = Form.useFormInstance();
	const postalLookup = isPostalEndpoint(endpoint);

	const options = useMemo(() => {
		const fetched = (selectProps.options ?? []) as {
			label: string;
			value: string;
			data?: AddressMasterData;
		}[];
		if (!value) return fetched;
		const exists = fetched.some((o) => o.value === value);
		return exists ? fetched : [{ label: value, value }, ...fetched];
	}, [selectProps.options, value]);

	const handleChange = (next: string | undefined, option: unknown) => {
		onChange?.(next);
		if (!postalLookup || !id) return;
		// option from AntD Select for object-typed option is the option entry itself.
		const data = (option as { data?: AddressMasterData } | undefined)?.data;
		const siblings = deriveAddressSiblings(id);
		if (!data || !siblings) return;
		form.setFieldsValue({
			[siblings.kelurahan]: data.locationName ?? '',
			[siblings.kecamatan]: data.districtName ?? '',
			[siblings.kota]: data.cityName ?? '',
		});
	};

	return (
		<Select
			{...selectProps}
			id={id}
			options={options}
			value={value}
			onChange={handleChange}
			disabled={disabled}
			placeholder="Ketik 3 digit kode pos..."
		/>
	);
};

export type AccordionSection = {
	key: string;
	title: string;
	fields?: FieldConfig[];
	render?: (readOnly: boolean) => ReactNode;
};

type QCAccordionFormProps = {
	sections: AccordionSection[];
	form: FormInstance;
	readOnly?: boolean;
	topFields?: FieldConfig[];
};

const DebugFieldTag: FC<{ name: string }> = ({ name }) => {
	const { fieldMeta, debugFieldMeta } = useQCPraPencairanData();
	if (!__IS_DEVELOPMENT__ || !debugFieldMeta) return null;
	const m = fieldMeta[name];
	const base: React.CSSProperties = { fontSize: 10, padding: '0 4px', borderRadius: 3, display: 'inline-block', marginRight: 2 };
	const apiPath = FIELD_API_PATHS[name] ?? name;
	const keyTag = <span style={{ ...base, background: '#f0f0f0', color: '#595959', border: '1px solid #d9d9d9', fontFamily: 'monospace' }}>{apiPath}</span>;
	if (!m) return (
		<div style={{ marginBottom: 2 }}>
			{keyTag}
			<span style={{ ...base, background: '#eee', color: '#999' }}>not in API</span>
		</div>
	);
	return (
		<div style={{ marginBottom: 2 }}>
			{keyTag}
			<span style={{ ...base, background: m.required ? '#fff1f0' : '#f6ffed', color: m.required ? '#cf1322' : '#389e0d', border: `1px solid ${m.required ? '#ffa39e' : '#b7eb8f'}` }}>
				req:{String(m.required)}
			</span>
			<span style={{ ...base, background: m.editable ? '#e6f7ff' : '#f5f5f5', color: m.editable ? '#096dd9' : '#8c8c8c', border: `1px solid ${m.editable ? '#91d5ff' : '#d9d9d9'}` }}>
				editable:{String(m.editable)}
			</span>
		</div>
	);
};

export const renderField = (field: FieldConfig, readOnly: boolean) => {
	const disabled = readOnly || !!field.disabled;
	if (field.sourceEndpoint) {
		return <LookupSelect endpoint={field.sourceEndpoint} disabled={disabled} />;
	}
	switch (field.type) {
		case 'number':
			return <InputNumber style={{ width: '100%' }} disabled={disabled} />;
		case 'currency':
			return <InputNumber {...inputCurrency} style={{ width: '100%' }} disabled={disabled} />;
		case 'select':
			return (
				<Select
					options={field.options ?? []}
					style={{ width: '100%' }}
					disabled={disabled}
					allowClear
				/>
			);
		case 'date':
			return <DatePicker style={{ width: '100%' }} disabled={disabled} format='DD/MM/YYYY' />;
		case 'textarea':
			return <Input.TextArea rows={4} disabled={disabled} />;
		case 'radio':
			return <Radio.Group options={field.options ?? []} disabled={disabled} />;
		case 'divider':
			return null;
		case 'text':
		default:
			return <Input disabled={disabled} />;
	}
};

export const buildRows = (fields: FieldConfig[]): FieldConfig[][] => {
	const rows: FieldConfig[][] = [];
	let currentRow: FieldConfig[] = [];
	let currentSpan = 0;

	for (const field of fields) {
		const span = field.span ?? 12;
		if (currentSpan + span > 24) {
			rows.push(currentRow);
			currentRow = [field];
			currentSpan = span;
		} else {
			currentRow.push(field);
			currentSpan += span;
			if (currentSpan >= 24) {
				rows.push(currentRow);
				currentRow = [];
				currentSpan = 0;
			}
		}
	}
	if (currentRow.length > 0) rows.push(currentRow);
	return rows;
};

export const renderRows = (rows: FieldConfig[][], readOnly: boolean) => (
	<>
		{rows.map((row, rowIndex) => (
			<Fragment key={rowIndex}>
				<Row gutter={[40, 20]}>
					{row.map((field) => (
						<Col key={field.name} span={field.span ?? 12}>
							{field.type === 'spacer' ? null : field.type === 'divider' ? (
								<Fragment>
									<Divider />
									<Typography.Title level={5} style={{ margin: '0px 0 10px', color: '#333' }}>
										{field.label}
									</Typography.Title>
								</Fragment>
							) : field.type === 'rt-rw' ? (
								<>
									<DebugFieldTag name={field.name} />
									<Form.Item
										label={field.label}
										labelCol={{ span: 8 }}
										wrapperCol={{ span: 16 }}
										required={field.required !== false}
									>
										<Row wrap={false} align="middle" gutter={8}>
											<Col flex="auto">
												<Form.Item name={field.name} noStyle rules={rules.rt}>
													<Input {...inputRtRw} disabled={readOnly || !!field.disabled} />
												</Form.Item>
											</Col>
											<Col flex="none">/</Col>
											<Col flex="auto">
												<Form.Item name={field.name.replace(/[Rr]t(?=[A-Z]|$)/, (m) => m[0] === 'R' ? 'Rw' : 'rw')} noStyle rules={rules.rw}>
													<Input {...inputRtRw} disabled={readOnly || !!field.disabled} />
												</Form.Item>
											</Col>
										</Row>
									</Form.Item>
								</>
							) : (
								<>
									<DebugFieldTag name={field.name} />
									<Form.Item
										name={field.name}
										label={field.label}
										labelCol={{ span: field.span === 24 ? 4 : 8 }}
										wrapperCol={{ span: field.span === 24 ? 20 : 16 }}
										rules={
											field.required !== false
												? [{ required: true, message: `${field.label} wajib diisi` }]
												: undefined
										}
									>
										{renderField(field, readOnly)}
									</Form.Item>
								</>
							)}
						</Col>
					))}
				</Row>
				{rowIndex < rows.length - 1 &&
					!row.some((f) => f.type === 'divider') &&
					!rows[rowIndex + 1].some((f) => f.type === 'divider') && (
						<Divider />
					)}
			</Fragment>
		))}
	</>
);

// ─── Manajemen & Komposisi Kepemilikan Saham Table ──────────────────────────

type SahamRow = {
	key: number;
	nama: string;
	tipeIdentitas: string;
	kewarganegaraan: string;
	nationality: string;
	jenisIdentitas: string;
	noId: string;
	tanggalLahir: string;
	jenisKelamin: string;
	jabatan: string;
	alamat: string;
	kodePos: string;
	kota: string;
	kecamatan: string;
	kelurahan: string;
	rtRw: string;
	noTelepon: string;
	status: string;
	kepemilikanSaham: string;
};

type SahamModalState = {
	open: boolean;
	mode: 'create' | 'edit';
	readOnly?: boolean;
	index?: number;
	record?: Partial<SahamFormValues>;
};

export const QCSahamTable: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const { saham, updateSahamRow } = useQCPraPencairanData();
	const [modalState, setModalState] = useState<SahamModalState>({ open: false, mode: 'create' });

	// Source of truth: context populated by API mapping
	const sahamData = saham.data;
	const sahamDataMeta = saham.meta;

	// Resolve label by id from select options (API returns IDs for select fields).
	// If value doesn't match any option (e.g. 0 placeholder), return blank — raw value is preserved on submit elsewhere.
	const labelFor = (idVal: unknown, options?: { label: string; value: string }[]): string => {
		if (idVal == null || idVal === '') return '';
		const found = options?.find((o) => o.value === String(idVal));
		return found?.label ?? '';
	};

	// Build display rows from raw form values + meta (for label resolution)
	const displayRows: SahamRow[] = sahamData.map((row, index) => {
		const meta = sahamDataMeta[index] ?? {};
		const tgl = row.tanggalLahir as { format?: (fmt: string) => string } | string | undefined;
		const tglStr = tgl && typeof tgl === 'object' && 'format' in tgl && tgl.format
			? tgl.format('DD/MM/YYYY')
			: typeof tgl === 'string' ? tgl : '';
		return {
			key: index,
			nama: (row.nama as string) ?? '',
			tipeIdentitas: '',
			kewarganegaraan: labelFor(row.kewarganegaraan, meta.kewarganegaraan?.options),
			nationality: (row.kebangsaan as string) ?? '',
			jenisIdentitas: labelFor(row.jenisIdentitas, meta.jenisIdentitas?.options),
			noId: (row.noIdentitas as string) ?? '',
			tanggalLahir: tglStr,
			jenisKelamin: labelFor(row.jenisKelamin, meta.jenisKelamin?.options),
			jabatan: labelFor(row.jabatan, meta.jabatan?.options),
			alamat: (row.alamat as string) ?? '',
			kodePos: (row.kodePos as string) ?? '',
			kota: (row.kota as string) ?? '',
			kecamatan: (row.kecamatan as string) ?? '',
			kelurahan: (row.kelurahan as string) ?? '',
			rtRw: `${row.rt ?? ''}/${row.rw ?? ''}`,
			noTelepon: (row.noTelepon as string) ?? '',
			status: labelFor(row.status, meta.status?.options),
			kepemilikanSaham: row.persentaseKepemilikan != null ? `${row.persentaseKepemilikan}%` : '',
		};
	});

	// Translate raw form row → modal record shape (raw values, ids preserved)
	const rowToRecord = (index: number): Partial<SahamFormValues> => {
		const row = sahamData[index] ?? {};
		return {
			nama: (row.nama as string) ?? '',
			kewarganegaraan: (row.kewarganegaraan as string) ?? '',
			nationality: (row.kebangsaan as string) ?? '',
			jenisIdentitas: (row.jenisIdentitas as string) ?? '',
			noId: (row.noIdentitas as string) ?? '',
			identityFile: row.identityFile as SahamFormValues['identityFile'],
			tanggalLahir: (row.tanggalLahir as SahamFormValues['tanggalLahir']) ?? null,
			jenisKelamin: (row.jenisKelamin as string) ?? '',
			noTelepon: (row.noTelepon as string) ?? '',
			jabatan: (row.jabatan as string) ?? '',
			alamat: (row.alamat as string) ?? '',
			kodePos: (row.kodePos as string) ?? '',
			kelurahan: (row.kelurahan as string) ?? '',
			kecamatan: (row.kecamatan as string) ?? '',
			kota: (row.kota as string) ?? '',
			rt: (row.rt as string) ?? '',
			rw: (row.rw as string) ?? '',
			status: (row.status as string) ?? '',
			isManagement: Boolean(row.isShareholder),
			kepemilikanSaham: (row.persentaseKepemilikan as number | null) ?? null,
		};
	};

	const handleEdit = (index: number) => {
		setModalState({ open: true, mode: 'edit', index, record: rowToRecord(index) });
	};

	const handleSave = (values: SahamFormValues) => {
		if (modalState.mode === 'edit' && modalState.index !== undefined && updateSahamRow) {
			const prev = sahamData[modalState.index] ?? {};
			updateSahamRow(modalState.index, {
				...prev,
				nama: values.nama,
				kewarganegaraan: values.kewarganegaraan,
				kebangsaan: values.nationality,
				jenisIdentitas: values.jenisIdentitas,
				noIdentitas: values.noId,
				tanggalLahir: values.tanggalLahir,
				jenisKelamin: values.jenisKelamin,
				noTelepon: values.noTelepon,
				jabatan: values.jabatan,
				alamat: values.alamat,
				kodePos: values.kodePos,
				kelurahan: values.kelurahan,
				kecamatan: values.kecamatan,
				kota: values.kota,
				rt: values.rt,
				rw: values.rw,
				status: values.status,
				isShareholder: values.isManagement,
				persentaseKepemilikan: values.kepemilikanSaham,
			});
		}
		setModalState({ open: false, mode: 'create' });
	};

	return (
		<>
			<Table<SahamRow>
				bordered
				pagination={false}
				scroll={{ x: 'max-content' }}
				dataSource={displayRows}
				columns={[
					{
						title: 'Action',
						width: 80,
						align: 'center',
						render: (_, _record, index) => (
							<div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
								<a
									onClick={(e) => {
										e.preventDefault();
										setModalState({ open: true, mode: 'edit', readOnly: true, index, record: rowToRecord(index) });
									}}
									title='Lihat'
								>
									<FileSearchOutlined />
								</a>
								{!readOnly && (
									<a
										onClick={(e) => {
											e.preventDefault();
											handleEdit(index);
										}}
										title='Edit'
									>
										<EditOutlined />
									</a>
								)}
							</div>
						),
					},
					{ title: 'Nama', dataIndex: 'nama' },
					{ title: 'Tipe Identitas', dataIndex: 'tipeIdentitas' },
					{ title: 'Kewarganegaraan', dataIndex: 'kewarganegaraan' },
					{ title: 'Nationality', dataIndex: 'nationality' },
					{ title: 'Jenis Identitas', dataIndex: 'jenisIdentitas' },
					{ title: 'No. ID', dataIndex: 'noId' },
					{ title: 'Tanggal Lahir', dataIndex: 'tanggalLahir' },
					{ title: 'Jenis Kelamin', dataIndex: 'jenisKelamin' },
					{ title: 'Jabatan', dataIndex: 'jabatan' },
					{ title: 'Alamat', dataIndex: 'alamat' },
					{ title: 'Kode Pos', dataIndex: 'kodePos' },
					{ title: 'Kota', dataIndex: 'kota' },
					{ title: 'Kecamatan', dataIndex: 'kecamatan' },
					{ title: 'Kelurahan', dataIndex: 'kelurahan' },
					{ title: 'RT/RW', dataIndex: 'rtRw' },
					{ title: 'No. Telepon', dataIndex: 'noTelepon' },
					{ title: 'Status', dataIndex: 'status' },
					{ title: 'Kepemilikan Saham (%)', dataIndex: 'kepemilikanSaham' },
				]}
			/>
			<QCSahamModal
				key={modalState.open ? `saham-${modalState.index ?? 'new'}` : 'closed'}
				open={modalState.open}
				mode={modalState.mode}
				readOnly={modalState.readOnly}
				record={modalState.record}
				meta={modalState.index !== undefined ? sahamDataMeta[modalState.index] : undefined}
				onClose={() => setModalState({ open: false, mode: 'create' })}
				onSave={handleSave}
			/>
		</>
	);
};

// ─── Legalitas Dokumen Editable Table ───────────────────────────────────────

const LEGALITAS_JENIS_OPTIONS = [
	{ label: 'Pilih', value: '' },
	{ label: 'Akte Awal', value: 'akte-awal' },
	{ label: 'NIB', value: 'nib' },
	{ label: 'NPWP', value: 'npwp' },
	{ label: 'TDP', value: 'tdp' },
	{ label: 'SKDP', value: 'skdp' },
	{ label: 'SIUP', value: 'siup' },
	{ label: 'Keterangan Domisili', value: 'keterangan-domisili' },
];

export const QCLegalitasTable: FC<{ readOnly: boolean }> = ({ readOnly }) => (
	<Form.List name='legalitasDokumen'>
		{(fields) => (
			<Table
				bordered
				pagination={false}
				dataSource={fields}
				rowKey='key'
				scroll={{ x: 'max-content' }}
				columns={[
					{
						title: 'No.',
						width: 50,
						align: 'center',
						render: (_: unknown, __: unknown, index: number) => index + 1,
					},
					{
						title: 'Jenis',
						width: 700,
						render: (_: unknown, field: { name: number }) => (
							<Form.Item name={[field.name, 'jenis']} noStyle>
								<Select options={LEGALITAS_JENIS_OPTIONS} style={{ width: '100%', maxWidth: 700 }} disabled={readOnly} />
							</Form.Item>
						),
					},
					{
						title: 'No Dokumen',
						width: 200,
						render: (_: unknown, field: { name: number }) => (
							<Form.Item name={[field.name, 'noDokumen']} noStyle>
								<Input disabled={readOnly} />
							</Form.Item>
						),
					},
					{
						title: 'Tanggal Dokumen',
						width: 180,
						render: (_: unknown, field: { name: number }) => (
							<Form.Item name={[field.name, 'tanggalDokumen']} noStyle>
								<DatePicker format='DD/MM/YYYY' disabled={readOnly} style={{ width: '100%' }} />
							</Form.Item>
						),
					},
					{
						title: 'Notes',
						render: (_: unknown, field: { name: number }) => (
							<Form.Item name={[field.name, 'notes']} noStyle>
								<Input disabled={readOnly} />
							</Form.Item>
						),
					},
				]}
			/>
		)}
	</Form.List>
);

// ─── Data Pemilik Manfaat Table ──────────────────────────────────────────────

type PemilikManfaatRow = {
	key: number;
	id?: number;
	hubunganDenganCustomer: string;
	nama: string;
	nik: string;
	npwp: string;
	tempatLahir: string;
	tanggalLahir: unknown;
	kewarganegaraan: string;
	negaraAsal: string;
	kodePos: string;
	kota: string;
	kecamatan: string;
	kelurahan: string;
	jenisKelamin: string;
	statusPerkawinan: string;
	jenisPekerjaan: string;
	namaPerusahaan: string;
};

const labelFor = (idVal: unknown, options?: { label: string; value: string }[]): string => {
	if (idVal == null || idVal === '') return '';
	const found = options?.find((o) => o.value === String(idVal));
	return found?.label ?? '';
};

const formatTanggal = (val: unknown): string => {
	if (!val) return '';
	if (typeof val === 'object' && 'format' in (val as object)) {
		return (val as { format: (f: string) => string }).format('DD/MM/YYYY');
	}
	return typeof val === 'string' ? val : '';
};

export const QCPemilikManfaatTable: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const form = Form.useFormInstance();
	const { beneficialOwnerOptions } = useQCPraPencairanData();
	const options = beneficialOwnerOptions ?? { relationship: [], nationality: [], gender: [], maritalStatus: [] };
	const data = (Form.useWatch('pemilikManfaatData', form) as PemilikManfaatRow[] | undefined) ?? [];

	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
	const [modalReadOnly, setModalReadOnly] = useState(false);
	const [editIndex, setEditIndex] = useState<number | null>(null);
	const [editRecord, setEditRecord] = useState<Partial<PemilikManfaatFormValues> | undefined>();

	const writeBack = (next: PemilikManfaatRow[]) => {
		form.setFieldValue('pemilikManfaatData', next);
	};

	const handleAdd = () => {
		setModalMode('create');
		setModalReadOnly(false);
		setEditIndex(null);
		setEditRecord(undefined);
		setModalOpen(true);
	};

	const rowToRecord = (row: PemilikManfaatRow): Partial<PemilikManfaatFormValues> => ({
		hubunganDenganCustomer: row.hubunganDenganCustomer,
		nama: row.nama,
		nik: row.nik,
		npwp: row.npwp,
		tempatLahir: row.tempatLahir,
		tanggalLahir: (row.tanggalLahir as PemilikManfaatFormValues['tanggalLahir']) ?? null,
		kewarganegaraan: row.kewarganegaraan,
		negaraAsal: row.negaraAsal,
		kodePos: row.kodePos,
		kelurahan: row.kelurahan,
		kecamatan: row.kecamatan,
		kota: row.kota,
		jenisKelamin: row.jenisKelamin,
		statusPerkawinan: row.statusPerkawinan,
		jenisPekerjaan: row.jenisPekerjaan,
		namaPerusahaan: row.namaPerusahaan,
	});

	const handleEdit = (index: number) => {
		setModalMode('edit');
		setModalReadOnly(false);
		setEditIndex(index);
		setEditRecord(rowToRecord(data[index]));
		setModalOpen(true);
	};

	const handleView = (index: number) => {
		setModalMode('edit');
		setModalReadOnly(true);
		setEditIndex(null);
		setEditRecord(rowToRecord(data[index]));
		setModalOpen(true);
	};

	const handleDelete = (index: number) => {
		const target = data[index];
		Modal.confirm({
			title: 'Konfirmasi Hapus',
			content: `Apakah anda yakin ingin menghapus pemilik manfaat ${target.nama} ini?`,
			okText: 'Ya',
			cancelText: 'Tidak',
			onOk: () => writeBack(data.filter((_, i) => i !== index)),
		});
	};

	const handleSave = (values: PemilikManfaatFormValues) => {
		if (modalMode === 'edit' && editIndex !== null) {
			const next = data.map((r, i) => i === editIndex ? { ...r, ...values } : r);
			writeBack(next);
		} else {
			const newRow: PemilikManfaatRow = {
				key: Date.now(),
				...values,
			};
			writeBack([...data, newRow]);
		}
		setModalOpen(false);
	};

	const displayRows = data.map((row, index) => ({
		...row,
		key: row.key ?? index,
		_index: index,
		_hubungan: labelFor(row.hubunganDenganCustomer, options.relationship),
		_kewarganegaraan: labelFor(row.kewarganegaraan, options.nationality),
		_jenisKelamin: labelFor(row.jenisKelamin, options.gender),
		_statusPerkawinan: labelFor(row.statusPerkawinan, options.maritalStatus),
		_tanggalLahir: formatTanggal(row.tanggalLahir),
	}));

	return (
		<>
			{!readOnly && (
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
					<a
						onClick={handleAdd}
						style={{ color: '#1557BC', fontWeight: 700, fontSize: 12 }}
					>
						+ Tambah Pemilik Manfaat
					</a>
				</div>
			)}
			<Table
				bordered
				pagination={false}
				scroll={{ x: 'max-content' }}
				dataSource={displayRows}
				columns={[
					{
						title: 'Action',
						width: 100,
						fixed: 'left',
						align: 'center',
						render: (_, record) => (
							<div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
								<a title='Lihat' onClick={() => handleView((record as { _index: number })._index)}>
									<FileSearchOutlined />
								</a>
								{!readOnly && (
									<>
										<a title='Edit' onClick={() => handleEdit((record as { _index: number })._index)}>
											<EditOutlined />
										</a>
										<a title='Hapus' onClick={() => handleDelete((record as { _index: number })._index)} style={{ color: '#E10101' }}>
											<DeleteOutlined />
										</a>
									</>
								)}
							</div>
						),
					},
					{ title: 'Hubungan Dengan Customer', dataIndex: '_hubungan', width: 300 },
					{ title: 'Nama', dataIndex: 'nama', width: 160 },
					{ title: 'NIK', dataIndex: 'nik', width: 120 },
					{ title: 'NPWP', dataIndex: 'npwp', width: 180 },
					{ title: 'Tempat Lahir', dataIndex: 'tempatLahir', width: 120 },
					{ title: 'Tanggal Lahir', dataIndex: '_tanggalLahir', width: 120 },
					{ title: 'Kewarganegaraan', dataIndex: '_kewarganegaraan', width: 140 },
					{ title: 'Negara Asal (Jika WNA)', dataIndex: 'negaraAsal', width: 180 },
					{ title: 'Kode Pos', dataIndex: 'kodePos', width: 100 },
					{ title: 'Kelurahan', dataIndex: 'kelurahan', width: 160 },
					{ title: 'Kecamatan', dataIndex: 'kecamatan', width: 140 },
					{ title: 'Kota', dataIndex: 'kota', width: 140 },
					{ title: 'Jenis Kelamin', dataIndex: '_jenisKelamin', width: 120 },
					{ title: 'Status Perkawinan', dataIndex: '_statusPerkawinan', width: 140 },
					{ title: 'Jenis Pekerjaan', dataIndex: 'jenisPekerjaan', width: 140 },
					{ title: 'Nama Perusahaan / Usaha Profesi', dataIndex: 'namaPerusahaan', width: 220 },
				]}
			/>
			<QCPemilikManfaatModal
				open={modalOpen}
				mode={modalMode}
				readOnly={modalReadOnly}
				record={editRecord}
				options={options}
				onClose={() => setModalOpen(false)}
				onSave={handleSave}
			/>
		</>
	);
};

// ─── Alamat Sesuai Akta (kode pos = address select) ─────────────────────────

export const QCAlamatAktaSection: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const form = Form.useFormInstance();
	const selectAddress = useSelectAddress();
	const { fieldMeta } = useQCPraPencairanData();

	const addressAkta = Form.useWatch('addressAkta', form) as AddressMasterData | undefined;

	const lc = { span: 8 } as const;
	const wc = { span: 16 } as const;

	const displayKelurahan = addressAkta?.locationName;
	const displayKecamatan = addressAkta?.districtName;
	const displayKota = addressAkta?.cityName;

	const isDisabled = (name: string, fallback = false) => {
		const m = fieldMeta[name];
		if (m) return readOnly || !m.editable;
		return readOnly || fallback;
	};
	const isRequired = (name: string, fallback = true) => {
		const m = fieldMeta[name];
		return m ? m.required : fallback;
	};

	return (
		<>
			<Row gutter={[40, 20]}>
				<Col span={24}>
					<DebugFieldTag name="alamatLengkapAkta" />
					<Form.Item
						name="alamatLengkapAkta"
						label="Alamat Lengkap"
						required={isRequired('alamatLengkapAkta')}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: 20 }}
					>
						<Input.TextArea rows={4} disabled={isDisabled('alamatLengkapAkta', true)} />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="rtAkta" />
					<Form.Item label="RT/RW" required={isRequired('rtAkta')} labelCol={lc} wrapperCol={wc}>
						<Row wrap={false} align="middle" gutter={8}>
							<Col flex="auto">
								<Form.Item name="rtAkta" noStyle>
									<Input {...inputRtRw} disabled={isDisabled('rtAkta', true)} />
								</Form.Item>
							</Col>
							<Col flex="none">/</Col>
							<Col flex="auto">
								<Form.Item name="rwAkta" noStyle>
									<Input {...inputRtRw} disabled={isDisabled('rwAkta', true)} />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="addressAkta" />
					<Form.Item
						name="addressAkta"
						label="Kode Pos"
						required={isRequired('addressAkta')}
						labelCol={lc}
						wrapperCol={wc}
						getValueFromEvent={(_, option) => (option as { data?: AddressMasterData } | undefined)?.data}
						getValueProps={(value: AddressMasterData | undefined) => {
							if (!value) return {};
							return { value: { value: value.postalId, label: value.postalCode } };
						}}
					>
						<Select {...selectAddress} disabled={isDisabled('addressAkta')} placeholder="Ketik 3 digit kode pos..." />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="kelurahanAkta" />
					<Form.Item label="Kelurahan" required={isRequired('kelurahanAkta')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKelurahan} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="kecamatanAkta" />
					<Form.Item label="Kecamatan" required={isRequired('kecamatanAkta')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKecamatan} disabled />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="kotaAkta" />
					<Form.Item label="Kota" required={isRequired('kotaAkta')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKota} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="noTeleponAkta" />
					<Form.Item
						name="noTeleponAkta"
						label="No. Telepon"
						required={isRequired('noTeleponAkta')}
						labelCol={lc}
						wrapperCol={wc}
					>
						<Input disabled={isDisabled('noTeleponAkta')} />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="noHandphoneAkta" />
					<Form.Item
						name="noHandphoneAkta"
						label="No. Handphone"
						required={isRequired('noHandphoneAkta')}
						labelCol={lc}
						wrapperCol={wc}
					>
						<Input disabled={isDisabled('noHandphoneAkta')} />
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

// ─── Alamat Operasional Perusahaan (with checkbox toggle) ──────────────────

export const QCAlamatOperasionalSection: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const [useAlamatLegal, setUseAlamatLegal] = useState(true);
	const form = Form.useFormInstance();
	const selectAddress = useSelectAddress();
	const { fieldMeta } = useQCPraPencairanData();

	// Sync checkbox state from API-loaded form value (runs once when value becomes available)
	const useAlamatLegalFormValue = Form.useWatch('useAlamatLegal', form) as string | undefined;
	const syncedRef = useRef(false);
	useEffect(() => {
		if (!syncedRef.current && useAlamatLegalFormValue !== undefined) {
			syncedRef.current = true;
			setUseAlamatLegal(useAlamatLegalFormValue === 'yes');
		}
	}, [useAlamatLegalFormValue]);

	// Watch source fields
	const alamatLengkapAkta = Form.useWatch('alamatLengkapAkta', form) as string | undefined;
	const rtAkta = Form.useWatch('rtAkta', form) as string | undefined;
	const rwAkta = Form.useWatch('rwAkta', form) as string | undefined;
	const addressAkta = Form.useWatch('addressAkta', form) as AddressMasterData | undefined;

	// Watch address object (single source of truth for kodepos + derived fields)
	const addressOperasional = Form.useWatch('addressOperasional', form) as AddressMasterData | undefined;

	// Sync from legal address when checkbox is checked.
	// QC Pra Pencairan populates the `addressAkta` composite (kodepos picker), so we copy that.
	// QC New Customer keeps legal address as flat individual fields from the BE response — when the
	// composite is absent, fall back to those flat form values so kelurahan/kecamatan/kota survive.
	// `noTeleponOperasional` is intentionally excluded — it stays editable and keeps its API value
	// regardless of the checkbox state.
	useEffect(() => {
		if (useAlamatLegal) {
			const fallbackAddress = {
				postalId: 0,
				postalCode: (form.getFieldValue('kodeposAkta') as string | undefined) || '',
				locationName: (form.getFieldValue('kelurahanAkta') as string | undefined) || '',
				districtName: (form.getFieldValue('kecamatanAkta') as string | undefined) || '',
				cityName: (form.getFieldValue('kotaAkta') as string | undefined) || '',
			};
			form.setFieldsValue({
				alamatLengkapOperasional: alamatLengkapAkta,
				rtOperasional: rtAkta,
				rwOperasional: rwAkta,
				addressOperasional: addressAkta ? { ...addressAkta } : fallbackAddress,
			});
		}
	}, [useAlamatLegal, alamatLengkapAkta, rtAkta, rwAkta, addressAkta, form]);

	const lc = { span: 8 } as const;
	const wc = { span: 16 } as const;

	const displayKelurahan = addressOperasional?.locationName;
	const displayKecamatan = addressOperasional?.districtName;
	const displayKota = addressOperasional?.cityName;

	// Combine API meta + checkbox/readOnly state. Checkbox forces disabled regardless of API.
	const isDisabled = (name: string, fallback = false) => {
		if (readOnly || useAlamatLegal) return true;
		const m = fieldMeta[name];
		if (m) return !m.editable;
		return fallback;
	};
	// Phone number stays editable regardless of the "use legal address" checkbox — it always
	// follows API editable meta only (and the page-level readOnly).
	const isPhoneDisabled = (name: string) => {
		if (readOnly) return true;
		const m = fieldMeta[name];
		if (m) return !m.editable;
		return false;
	};
	const isRequired = (name: string, fallback = true) => {
		const m = fieldMeta[name];
		return m ? m.required : fallback;
	};

	return (
		<>
			<Checkbox
				checked={useAlamatLegal}
				onChange={(e) => setUseAlamatLegal(e.target.checked)}
				disabled={readOnly}
				style={{ marginBottom: 16, marginLeft: '16.667%' }}
			>
				Gunakan alamat sesuai{' '}
				<Typography.Text strong style={{ color: '#1557BC' }}>
					Alamat Legal Perusahaan
				</Typography.Text>
			</Checkbox>

			<Row gutter={[40, 20]}>
				<Col span={24}>
					<DebugFieldTag name="alamatLengkapOperasional" />
					<Form.Item
						name="alamatLengkapOperasional"
						label="Alamat Lengkap"
						required={isRequired('alamatLengkapOperasional')}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: 20 }}
					>
						<Input.TextArea rows={4} disabled={isDisabled('alamatLengkapOperasional')} />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="rtOperasional" />
					<Form.Item label="RT/RW" required={isRequired('rtOperasional')} labelCol={lc} wrapperCol={wc}>
						<Row wrap={false} align="middle" gutter={8}>
							<Col flex="auto">
								<Form.Item name="rtOperasional" noStyle rules={isDisabled('rtOperasional') ? [] : rules.rt}>
									<Input {...inputRtRw} disabled={isDisabled('rtOperasional')} />
								</Form.Item>
							</Col>
							<Col flex="none">/</Col>
							<Col flex="auto">
								<Form.Item name="rwOperasional" noStyle rules={isDisabled('rwOperasional') ? [] : rules.rw}>
									<Input {...inputRtRw} disabled={isDisabled('rwOperasional')} />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="addressOperasional" />
					<Form.Item
						name="addressOperasional"
						label="Kode Pos"
						required={isRequired('addressOperasional')}
						labelCol={lc}
						wrapperCol={wc}
						getValueFromEvent={(_, option) => (option as { data?: AddressMasterData } | undefined)?.data}
						getValueProps={(value: AddressMasterData | undefined) => {
							if (!value) return {};
							return { value: { value: value.postalId, label: value.postalCode } };
						}}
					>
						<Select {...selectAddress} disabled={isDisabled('addressOperasional')} placeholder="Ketik 3 digit kode pos..." />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="kelurahanOperasional" />
					<Form.Item label="Kelurahan" required={isRequired('kelurahanOperasional')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKelurahan} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="kecamatanOperasional" />
					<Form.Item label="Kecamatan" required={isRequired('kecamatanOperasional')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKecamatan} disabled />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="kotaOperasional" />
					<Form.Item label="Kota" required={isRequired('kotaOperasional')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKota} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="noTeleponOperasional" />
					<Form.Item
						name="noTeleponOperasional"
						label="No. Telepon"
						required={isRequired('noTeleponOperasional')}
						labelCol={lc}
						wrapperCol={wc}
					>
						<Input disabled={isPhoneDisabled('noTeleponOperasional')} />
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

// ─── Gudang / Kantor Perwakilan / Workshop (with checkbox toggle) ────────────

const GUDANG_STATUS_OPTIONS = [
	{ label: 'Kontrak Kurang dari Tenor', value: 'kontrak-kurang-dari-tenor' },
	{ label: 'Kontrak Lebih dari Tenor', value: 'kontrak-lebih-dari-tenor' },
	{ label: 'Kredit', value: 'kredit' },
	{ label: 'Milik Owner / Shareholder', value: 'milik-owner-shareholder' },
	{ label: 'Milik Perusahaan', value: 'milik-perusahaan' },
	{ label: 'Nihil', value: 'nihil' },
];

export const QCGudangAddressSection: FC<{ readOnly: boolean }> = ({ readOnly }) => {
	const [useAlamatLegal, setUseAlamatLegal] = useState(true);
	const form = Form.useFormInstance();
	const selectAddress = useSelectAddress();
	const { fieldMeta } = useQCPraPencairanData();

	// Gudang section doesn't have a corresponding API field for "use same address",
	// so keep default true (synced to legal address unless user unchecks)
	const gudangStatusOptions = fieldMeta['statusKepemilikanGudang']?.options ?? GUDANG_STATUS_OPTIONS;

	const isRequired = (name: string, fallback = true) => {
		const m = fieldMeta[name];
		return m ? m.required : fallback;
	};

	// Watch source fields
	const alamatLengkapAkta = Form.useWatch('alamatLengkapAkta', form) as string | undefined;
	const rtAkta = Form.useWatch('rtAkta', form) as string | undefined;
	const rwAkta = Form.useWatch('rwAkta', form) as string | undefined;
	const addressAkta = Form.useWatch('addressAkta', form) as AddressMasterData | undefined;
	const noTelepon = Form.useWatch('noTelepon', form) as string | undefined;

	const addressGudang = Form.useWatch('addressGudang', form) as AddressMasterData | undefined;

	useEffect(() => {
		if (useAlamatLegal) {
			form.setFieldsValue({
				alamatGudang: alamatLengkapAkta,
				rtGudang: rtAkta,
				rwGudang: rwAkta,
				noTeleponGudang: noTelepon,
				addressGudang: addressAkta ? { ...addressAkta } : {
					postalId: 0,
					postalCode: '',
					locationName: '',
					districtName: '',
					cityName: '',
				},
			});
		}
	}, [useAlamatLegal, alamatLengkapAkta, rtAkta, rwAkta, noTelepon, addressAkta, form]);

	const isDisabled = readOnly || useAlamatLegal;
	const lc = { span: 8 } as const;
	const wc = { span: 16 } as const;

	const displayKelurahan = addressGudang?.locationName;
	const displayKecamatan = addressGudang?.districtName;
	const displayKota = addressGudang?.cityName;

	return (
		<>
			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="statusKepemilikanGudang" />
					<Form.Item
						name="statusKepemilikanGudang"
						label="Status Kepemilikan Workshop"
						required={isRequired('statusKepemilikanGudang')}
						labelCol={lc}
						wrapperCol={wc}
					>
						<Select options={gudangStatusOptions} disabled={readOnly} />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Checkbox
				checked={useAlamatLegal}
				onChange={(e) => setUseAlamatLegal(e.target.checked)}
				disabled={readOnly}
				style={{ marginBottom: 16, marginLeft: '16.667%' }}
			>
				Gunakan alamat sesuai{' '}
				<Typography.Text strong style={{ color: '#1557BC' }}>
					Alamat Legal Perusahaan
				</Typography.Text>
			</Checkbox>

			<Row gutter={[40, 20]}>
				<Col span={24}>
					<DebugFieldTag name="alamatGudang" />
					<Form.Item
						name="alamatGudang"
						label="Alamat Lengkap"
						required={isRequired('alamatGudang')}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: 20 }}
					>
						<Input.TextArea rows={4} disabled={isDisabled} />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="rtGudang" />
					<Form.Item label="RT/RW" required={isRequired('rtGudang')} labelCol={lc} wrapperCol={wc}>
						<Row wrap={false} align="middle" gutter={8}>
							<Col flex="auto">
								<Form.Item name="rtGudang" noStyle rules={isDisabled ? [] : rules.rt}>
									<Input {...inputRtRw} disabled={isDisabled} />
								</Form.Item>
							</Col>
							<Col flex="none">/</Col>
							<Col flex="auto">
								<Form.Item name="rwGudang" noStyle rules={isDisabled ? [] : rules.rw}>
									<Input {...inputRtRw} disabled={isDisabled} />
								</Form.Item>
							</Col>
						</Row>
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="kodeposGudang" />
					<Form.Item
						name="addressGudang"
						label="Kode Pos"
						required={isRequired('kodeposGudang')}
						labelCol={lc}
						wrapperCol={wc}
						getValueFromEvent={(_, option) => (option as { data?: AddressMasterData } | undefined)?.data}
						getValueProps={(value: AddressMasterData | undefined) => {
							if (!value) return {};
							return { value: { value: value.postalId, label: value.postalCode } };
						}}
					>
						<Select {...selectAddress} disabled={isDisabled} placeholder="Ketik 3 digit kode pos..." />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="kelurahanGudang" />
					<Form.Item label="Kelurahan" required={isRequired('kelurahanGudang')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKelurahan} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="kecamatanGudang" />
					<Form.Item label="Kecamatan" required={isRequired('kecamatanGudang')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKecamatan} disabled />
					</Form.Item>
				</Col>
			</Row>

			<Divider />

			<Row gutter={[40, 20]}>
				<Col span={12}>
					<DebugFieldTag name="kotaGudang" />
					<Form.Item label="Kota" required={isRequired('kotaGudang')} labelCol={lc} wrapperCol={wc}>
						<Input value={displayKota} disabled />
					</Form.Item>
				</Col>
				<Col span={12}>
					<DebugFieldTag name="noTeleponGudang" />
					<Form.Item
						name="noTeleponGudang"
						label="No. Telepon"
						required={isRequired('noTeleponGudang')}
						labelCol={lc}
						wrapperCol={wc}
					>
						<Input disabled={isDisabled} />
					</Form.Item>
				</Col>
			</Row>
		</>
	);
};

// ─── Top Fields (Data Customer) ─────────────────────────────────────────────

export const QC_CUSTOMER_TOP_FIELDS: FieldConfig[] = [
	{ name: 'namaSesuaiAkta', label: 'Nama Sesuai Akta', type: 'text', disabled: true, required: true },
	{ name: 'npwp', label: 'NPWP', type: 'text', disabled: true, required: true },
	{ name: 'namaDataBank', label: 'Nama untuk Data Bank', type: 'text', required: true },
	{ name: '_alamatDivider', label: 'Alamat', type: 'divider', span: 24 },
	{ name: 'alamatLengkap', label: 'Alamat Lengkap', type: 'textarea', disabled: true, span: 24 },
	{ name: 'rt', label: 'RT/RW', type: 'rt-rw', disabled: true },
	{ name: 'kodePos', label: 'Kode Pos', type: 'text', disabled: true },
	{ name: 'kelurahan', label: 'Kelurahan', type: 'text', disabled: true },
	{ name: 'kecamatan', label: 'Kecamatan', type: 'text', disabled: true },
	{ name: 'kota', label: 'Kota', type: 'text', disabled: true },
	{ name: 'noTelepon', label: 'No. Telepon', type: 'text', required: true },
];

// ─── Accordion Sections ──────────────────────────────────────────────────────

export const QC_NEW_CUSTOMER_SECTIONS: AccordionSection[] = [
	{
		key: 'data-perusahaan',
		title: 'Data Perusahaan',
		fields: [
			{ name: 'tempatAktaDiterbitkan', label: 'Tempat Akta Diterbitkan', type: 'text', required: true },
			{ name: 'tahunPendirian', label: 'Tahun Pendirian Sesuai Akta', type: 'text', required: true },
			{ name: 'jenisPerusahaan', label: 'Jenis Perusahaan', type: 'text', disabled: true, required: true },
			{ name: 'emailPerusahaan', label: 'Email Perusahaan', type: 'text' },
			{ name: 'jumlahKaryawan', label: 'Jumlah Karyawan', type: 'number', required: true },
			{ name: 'groupBmpp', label: 'Group BMPP', type: 'text', disabled: true, required: true },
			{
				name: 'targetMarket', label: 'Target Market', type: 'select', required: true,
				options: [],
			},
			{
				name: 'isGoPublic', label: 'Go Public / Terbuka', type: 'radio',
				options: [
					{ label: 'Yes', value: 'yes' },
					{ label: 'No', value: 'no' },
				],
			},
			{
				name: 'statusPerusahaan', label: 'Status', type: 'select', required: true,
				options: [],
			},
			{ name: '_spacerStatus', label: '', type: 'spacer' },
		],
	},
	{
		key: 'alamat-akta',
		title: 'Alamat Sesuai Akta',
		fields: [
			{ name: 'alamatLengkapAkta', label: 'Alamat Lengkap', type: 'textarea', disabled: true, span: 24 },
			{ name: 'rtAkta', label: 'RT/RW', type: 'rt-rw', disabled: true },
			{ name: 'kodeposAkta', label: 'Kode Pos', type: 'text', disabled: true },
			{ name: 'kelurahanAkta', label: 'Kelurahan', type: 'text', disabled: true },
			{ name: 'kecamatanAkta', label: 'Kecamatan', type: 'text', disabled: true },
			{ name: 'kotaAkta', label: 'Kota', type: 'text', disabled: true },
			{ name: 'noTeleponAkta', label: 'No. Telepon', type: 'text' },
			{ name: 'noHandphoneAkta', label: 'No. Handphone', type: 'text' },
		],
	},
	{
		key: 'alamat-operasional',
		title: 'Alamat Operasional Perusahaan',
		render: (readOnly) => <QCAlamatOperasionalSection readOnly={readOnly} />,
	},
	{
		key: 'data-keuangan',
		title: 'Data Keuangan',
		fields: [
			// Row 1
			{ name: 'penghasilanPerBulan', label: 'Penghasilan Per Bulan', type: 'currency' },
			{ name: 'rasioLancar', label: 'Rasio Lancar (%)', type: 'number' },
			// Row 2
			{ name: 'tingkatRoi', label: 'Tingkat Pengembalian Investasi (ROI)', type: 'number' },
			{ name: 'rasioAngsuranPenghasilan', label: 'Rasio Angsuran Dengan Penghasilan (%)', type: 'number' },
			// Row 3
			{
				name: 'jenisRekeningBank', label: 'Jenis Rekening Bank', type: 'select',
				options: [
					{ label: 'Non Loan Account', value: 'non-loan-account' },
					{ label: 'Loan Account', value: 'loan-account' },
				],
			},
			{ name: 'rataRataSaldoAkhir', label: 'Rata-Rata Saldo Akhir', type: 'currency' },
			// Row 4
			{ name: 'rataRataSaldoMengendap', label: 'Rata-Rata Saldo Mengendap', type: 'currency' },
			{ name: 'kasSetaraKas', label: 'Kas dan Setara Kas (Aset Lancar)', type: 'currency' },
			// Row 5
			{ name: 'piutangUsahaLancar', label: 'Piutang Usaha/Pembiayaan (Aset Lancar)', type: 'currency' },
			{ name: 'investasiKeuanganLancar', label: 'Investasi/Aset Keuangan Lainnya (Aset Lancar)', type: 'currency' },
			// Row 6
			{ name: 'asetLancarLainnya', label: 'Aset Lancar Lainnya (Aset Lancar)', type: 'currency' },
			{ name: 'totalAsetLancar', label: 'Total Aset Lancar', type: 'currency' },
			// Row 7
			{ name: 'piutangUsahaTidakLancar', label: 'Piutang Usaha/Pembiayaan (Aset Tidak Lancar)', type: 'currency' },
			{ name: 'investasiKeuanganTidakLancar', label: 'Investasi/Aset Keuangan Lainnya (Aset Tidak Lancar)', type: 'currency' },
			// Row 8
			{ name: 'asetTidakLancarLainnya', label: 'Aset Tidak Lancar Lainnya (Aset Tidak Lancar)', type: 'currency' },
			{ name: 'totalAsetTidakLancar', label: 'Total Aset Tidak Lancar', type: 'currency' },
			// Row 9 (alone)
			{ name: 'totalAset', label: 'Total Aset', type: 'currency' },
			{ name: '_spacerTotalAset', label: '', type: 'spacer' },
			// Row 10
			{
				name: 'statusTempatUsaha', label: 'Status Tempat Usaha', type: 'select',
				options: [
					{ label: 'Kontrak Kurang dari Tenor', value: 'kontrak-kurang-tenor' },
					{ label: 'Kontrak Lebih dari Tenor', value: 'kontrak-lebih-tenor' },
					{ label: 'Kredit', value: 'kredit' },
					{ label: 'Milik Owner / Shareholder', value: 'milik-owner' },
					{ label: 'Milik Perusahaan', value: 'milik-perusahaan' },
				],
			},
			{ name: 'menempatiTempatUsahaSejak', label: 'Menempati Tempat Usaha Sejak Tahun', type: 'text', required: false },
			// Row 11
			{ name: 'tanggalBerakhirnyaSewa', label: 'Tanggal Berakhirnya Sewa', type: 'date', required: false },
			{ name: 'tahunLaporan', label: 'Tahun Laporan', type: 'text', required: false },
			// Row 12
			{ name: 'aktivaLancar', label: 'Aktiva Lancar/ Aset Tidak Tetap', type: 'currency', required: false },
			{ name: 'totalHutang', label: 'Total Hutang', type: 'currency', required: false },
			// Row 13 (alone)
			{ name: 'hutangJangkaPendek', label: 'Hutang Jangka Pendek', type: 'currency', required: false },
			{ name: '_spacerHutang', label: '', type: 'spacer' },
			// Row 14
			{ name: 'modalDisetor', label: 'Modal Disetor', type: 'currency' },
			{ name: 'nilaiPenjualan', label: 'Nilai Penjualan', type: 'currency' },
			// Row 15
			{ name: 'pendapatanOperasional', label: 'Pendapatan Operasional', type: 'currency', required: false },
			{ name: 'labaRugiBruto', label: 'Laba/Rugi Bruto', type: 'currency' },
			// Row 16
			{ name: 'pendapatanNonOperasional', label: 'Pendapatan Non Operasional', type: 'currency', required: false },
			{ name: 'labaRugiSebelumPajak', label: 'Laba/Rugi Sebelum Pajak', type: 'currency' },
			// Row 17
			{ name: 'labaRugiTahunBerjalan', label: 'Laba Rugi - Tahun Berjalan', type: 'currency', required: false },
			{
				name: 'statusAudit', label: 'Status Audit', type: 'radio', required: false,
				options: [
					{ label: 'Yes', value: 'yes' },
					{ label: 'No', value: 'no' },
				],
			},
			// Row 18
			{ name: 'diAuditOleh', label: 'Di Audit Oleh', type: 'text', required: false },
			{ name: 'pinjamanJangkaPendek', label: 'Pinjaman Jangka Pendek (Liabilitas Jangka Pendek)', type: 'currency' },
			// Row 19
			{ name: 'utangUsahaJangkaPendek', label: 'Utang Usaha (Liabilitas Jangka Pendek)', type: 'currency' },
			{ name: 'liabilitasJangkaPendekLainnya', label: 'Liabilitas Jangka Pendek Lainnya (Liabilitas Jangka Pendek)', type: 'currency' },
			// Row 20
			{ name: 'liabilitasJangkaPendek', label: 'Liabilitas Jangka Pendek', type: 'currency', required: false },
			{ name: 'pinjamanJangkaPanjang', label: 'Pinjaman Jangka Panjang (Liabilitas Jangka Panjang)', type: 'currency' },
			// Row 21
			{ name: 'utangUsahaJangkaPanjang', label: 'Utang Usaha (Liabilitas Jangka Panjang)', type: 'currency' },
			{ name: 'liabilitasJangkaPanjangLainnya', label: 'Liabilitas Jangka Panjang Lainnya (Liabilitas Jangka Panjang)', type: 'currency' },
			// Row 22
			{ name: 'liabilitasJangkaPanjang', label: 'Liabilitas Jangka Panjang', type: 'currency', required: false },
			{ name: 'liabilitas', label: 'Liabilitas', type: 'currency', required: false },
			// Row 23
			{ name: 'ekuitas', label: 'Ekuitas', type: 'currency' },
			{ name: 'totalPassiva', label: 'Total Passiva', type: 'currency', required: false },
		],
	},
	{
		key: 'gudang-kantor',
		title: 'Gudang / Kantor Perwakilan / Workshop',
		fields: [
			{
				name: 'statusKepemilikanGudang', label: 'Status Kepemilikan Workshop', type: 'select',
				options: [
					{ label: 'Kontrak Kurang dari Tenor', value: 'kontrak-kurang-dari-tenor' },
					{ label: 'Kontrak Lebih dari Tenor', value: 'kontrak-lebih-dari-tenor' },
					{ label: 'Kredit', value: 'kredit' },
					{ label: 'Milik Owner / Shareholder', value: 'milik-owner-shareholder' },
					{ label: 'Milik Perusahaan', value: 'milik-perusahaan' },
					{ label: 'Nihil', value: 'nihil' },
				],
			},
			{ name: 'alamatGudang', label: 'Alamat Lengkap', type: 'textarea', disabled: true, span: 24 },
			{ name: 'rtGudang', label: 'RT/RW', type: 'rt-rw', disabled: true },
			{ name: 'kodeposGudang', label: 'Kode Pos', type: 'text', disabled: true },
			{ name: 'kelurahanGudang', label: 'Kelurahan', type: 'text', disabled: true },
			{ name: 'kecamatanGudang', label: 'Kecamatan', type: 'text', disabled: true },
			{ name: 'kotaGudang', label: 'Kota', type: 'text', disabled: true },
			{ name: 'noTeleponGudang', label: 'No. Telepon', type: 'text', disabled: true },
		],
	},
	{
		key: 'rekening-bank',
		title: 'Rekening Bank',
		fields: [
			{ name: 'namaBank', label: 'Nama Bank', type: 'text', disabled: true },
			{ name: 'namaCabangBank', label: 'Nama Cabang', type: 'text', disabled: true },
			{ name: 'nomorRekening', label: 'No. Rekening', type: 'text', disabled: true },
		],
	},
	{
		key: 'data-lain',
		title: 'Data Lain',
		fields: [
			{
				name: 'setujuPemasaranPihakKetiga', label: 'Setuju Pemasaran oleh Pihak Ketiga via Sarana Komunikasi Pribadi', type: 'radio', required: true, span: 24,
				options: [
					{ label: 'Ya', value: 'yes' },
					{ label: 'Tidak', value: 'no' },
				],
			},
			{
				name: 'pernahKreditMobil', label: 'Apakah Sebelumnya Pernah Mengajukan Kredit Mobil ?', type: 'radio', required: true,
				options: [
					{ label: 'Ya', value: 'yes' },
					{ label: 'Tidak', value: 'no' },
				],
			},
			{
				name: 'daftarHitamAppi', label: 'Daftar Hitam APPI', type: 'radio', required: true,
				options: [
					{ label: 'Ya', value: 'yes' },
					{ label: 'Tidak', value: 'no' },
				],
			},
			{
				name: 'setujuPemrosesan', label: 'Setuju Pemrosesan Data untuk Pembiayaan', type: 'radio', required: true,
				options: [
					{ label: 'Ya', value: 'yes' },
					{ label: 'Tidak', value: 'no' },
				],
			},
			{
				name: 'setujuPemasaranMtf', label: 'Setuju Pemasaran oleh MTF via Sarana Komunikasi Pribadi', type: 'radio', required: true,
				options: [
					{ label: 'Ya', value: 'yes' },
					{ label: 'Tidak', value: 'no' },
				],
			},
		],
	},
	{
		key: 'pic-ttd',
		title: 'PIC Tanda Tangan Dokumen',
		fields: [
			{ name: 'namaPicTtd', label: 'Nama', type: 'text', required: true },
			{ name: '_spacerPicTtd', label: '', type: 'spacer' },
			{ name: 'alamatLengkapPicTtd', label: 'Alamat Lengkap', type: 'textarea', span: 24, required: true },
			{ name: 'noKtpPicTtd', label: 'No KTP', type: 'text', required: true },
			{ name: 'jabatanPicTtd', label: 'Jabatan', type: 'text', required: true },
		],
	},
	{
		key: 'pic-penagihan',
		title: 'PIC Penagihan',
		fields: [
			{ name: 'namaPicPenagihan', label: 'Nama', type: 'text', required: true },
			{ name: 'jabatanPicPenagihan', label: 'Jabatan', type: 'text' },
			{ name: 'noTeleponPicPenagihan', label: 'No. Telepon', type: 'text' },
			{ name: 'noTelepon2PicPenagihan', label: 'No. Telepon 2', type: 'text' },
		],
	},
	{
		key: 'manajemen-saham',
		title: 'Manajemen & Komposisi Kepemilikan Saham',
		render: (readOnly) => <QCSahamTable readOnly={readOnly} />,
	},
	{
		key: 'legalitas-dokumen',
		title: 'Legalitas Dokumen',
		render: (readOnly) => <QCLegalitasTable readOnly={readOnly} />,
	},
];

// ─── Component ───────────────────────────────────────────────────────────────

const QCAccordionForm: FC<QCAccordionFormProps> = ({ sections, readOnly = false, topFields }) => {
	const items = sections.map((section) => ({
		key: section.key,
		label: section.title,
		children: section.render
			? section.render(readOnly)
			: renderRows(buildRows(section.fields ?? []), readOnly),
	}));

	return (
		<>
			{topFields && (
				<div style={{ marginBottom: 16 }}>
					{renderRows(buildRows(topFields), readOnly)}
				</div>
			)}
			<Collapse
				items={items}
				defaultActiveKey={sections.map((s) => s.key)}
			/>
		</>
	);
};

export default QCAccordionForm;
